import { ISearchService } from '@agent/core/search/search.service';
import { isArray, isString } from 'angular';

import {
  buildEAFilter,
  CriteriaOperator,
  distinct,
  EAFilter,
  EARange,
  EpticaFields,
  FilterOperator,
  RequestFields,
  Skill,
} from '@contact/upgrade';

import { PriorityType } from '../mail-basket.model';

export interface BasketFilter {
  build(): EAFilter;
  isEmpty(): boolean;
}

export class SubjectFilter implements BasketFilter {
  constructor(protected readonly searchService: ISearchService, public readonly value: string = '') {}

  build(): EAFilter {
    const field = buildEAFilter('string', 'lastMessageEvent.subject', this.value, CriteriaOperator.CONTAINS);
    const subjectWithoutAccentuatedChars = this.searchService.normalize(this.value);
    const fieldWithoutAccentuatedChars = buildEAFilter(
      'string',
      'lastMessageEvent.subject',
      subjectWithoutAccentuatedChars,
      CriteriaOperator.CONTAINS
    );
    return {
      operator: FilterOperator.OR,
      subFilters: [
        { operator: FilterOperator.AND, fields: [field] },
        { operator: FilterOperator.AND, fields: [fieldWithoutAccentuatedChars] },
      ],
    };
  }

  isEmpty(): boolean {
    return !isString(this.value) || this.value.length === 0;
  }
}

export class RequestIdFilter implements BasketFilter {
  constructor(public readonly value: string = '') {}

  build(): EAFilter {
    const field = buildEAFilter('long', 'id', this.value as any as number, CriteriaOperator.MATCH);
    return {
      operator: FilterOperator.AND,
      fields: [field],
    };
  }

  isEmpty(): boolean {
    return !isString(this.value) || this.value.length === 0;
  }
}

export class SenderFilter implements BasketFilter {
  constructor(public value: string = '') {}

  build(): EAFilter {
    const field = buildEAFilter('string', 'lastCustomer.customer_EMail', this.value, CriteriaOperator.CONTAINS);
    return {
      operator: FilterOperator.AND,
      fields: [field],
    };
  }

  isEmpty(): boolean {
    return !isString(this.value) || this.value.length === 0;
  }
}

export class DomainFilter implements BasketFilter {
  constructor(public readonly value: string = '') {}

  build(): EAFilter {
    const field = buildEAFilter('string', 'lastCustomer.customer_EMail', this.value, CriteriaOperator.CONTAINS);
    return {
      operator: FilterOperator.AND,
      fields: [field],
    };
  }

  isEmpty(): boolean {
    return !isString(this.value) || this.value.length === 0;
  }
}

export class RecipientFilter implements BasketFilter {
  constructor(public readonly value: string = '') {}

  build(): EAFilter {
    const field = buildEAFilter('string', RequestFields.RECIPIENT, this.value, CriteriaOperator.CONTAINS);
    const ccField = buildEAFilter('string', RequestFields.CC_RECIPIENT, this.value, CriteriaOperator.CONTAINS);
    return {
      operator: FilterOperator.OR,

      subFilters: [
        { operator: FilterOperator.AND, fields: [field] },
        { operator: FilterOperator.AND, fields: [ccField] },
      ],
    };
  }

  isEmpty(): boolean {
    return !isString(this.value) || this.value.length === 0;
  }
}

export class ReasonFilter implements BasketFilter {
  constructor(public readonly value: string = '') {}

  build(): EAFilter {
    const field = buildEAFilter('string', RequestFields.OWI_REASON, this.value, CriteriaOperator.CONTAINS);
    return {
      operator: FilterOperator.AND,
      fields: [field],
    };
  }

  isEmpty(): boolean {
    return !isString(this.value) || this.value.length === 0;
  }
}

export class SkillsFilter implements BasketFilter {
  constructor(public readonly values: Skill[] = []) {}

  build(): EAFilter {
    const field = buildEAFilter(
      'multi-string',
      RequestFields.SKILL,
      this.values.map(mediaSkill => mediaSkill.id),
      CriteriaOperator.CONTAINS
    );
    return {
      operator: FilterOperator.AND,
      fields: [field],
    };
  }

  isEmpty(): boolean {
    return !isArray(this.values) || this.values.length === 0;
  }
}

export class AttachmentFilter implements BasketFilter {
  constructor(public readonly enabled: boolean = false, public readonly hasAttachment: boolean = true) {}

  build(): EAFilter {
    const operator = this.hasAttachment ? CriteriaOperator.MATCH : CriteriaOperator.NOT_CONTAINS;
    const field = buildEAFilter('string', RequestFields.HAS_ATTACHMENT, 'true', operator);
    return {
      operator: FilterOperator.AND,
      fields: [field],
    };
  }

  isEmpty(): boolean {
    return !this.enabled;
  }
}

export class PeriodFilter implements BasketFilter {
  constructor(
    public readonly beginTime: Date = new Date(),
    public readonly endTime: Date = new Date(),
    public readonly isPeriodFilterEnabled: boolean = false
  ) {}

  build(): EAFilter {
    const dateRange: EARange<number> = {
      from: this.beginTime.getTime(),
      to: this.endTime.getTime(),
      fromIncluded: true,
      toIncluded: true,
    };
    const fieldRange = buildEAFilter('date-range', EpticaFields.CREATION_DATE, dateRange);
    return {
      operator: FilterOperator.AND,
      fields: [fieldRange],
    };
  }

  isEmpty(): boolean {
    return !this.isPeriodFilterEnabled;
  }
}

export class SendPeriodFilter implements BasketFilter {
  constructor(
    public readonly beginTime: Date = new Date(),
    public readonly endTime: Date = new Date(),
    public readonly isPeriodFilterEnabled: boolean = false
  ) {}

  build(): EAFilter {
    const dateRange: EARange<number> = {
      from: this.beginTime.getTime(),
      to: this.endTime.getTime(),
      fromIncluded: true,
      toIncluded: true,
    };
    const answerType = buildEAFilter('string', EpticaFields.EVENT_TYPE, 'answer_sent', CriteriaOperator.MATCH);
    const outGoingMailType = buildEAFilter('string', EpticaFields.EVENT_TYPE, 'oe_mail_sent', CriteriaOperator.MATCH);
    const fieldRange = buildEAFilter('date-range', EpticaFields.EVENT_DATE, dateRange);
    return {
      operator: FilterOperator.AND,
      nested: {
        path: EpticaFields.EVENT,
      },
      fields: [fieldRange],
      subFilters: [
        {
          operator: FilterOperator.OR,
          fields: [answerType, outGoingMailType],
        },
      ],
    };
  }

  isEmpty(): boolean {
    return !this.isPeriodFilterEnabled;
  }
}

export class PriorityFilter implements BasketFilter {
  private values: PriorityType[] = [];
  private PRIORITIES = {
    [PriorityType.NORMAL]: {
      order: 0,
      bottomValue: 0,
      upperValue: 400,
    },
    [PriorityType.MEDIUM]: {
      order: 1,
      bottomValue: 401,
      upperValue: 800,
    },
    [PriorityType.HIGH]: {
      order: 2,
      bottomValue: 801,
      upperValue: Infinity,
    },
  };

  setValues(values: PriorityType[]): PriorityFilter {
    this.values = distinct(values);
    return this;
  }

  buildPriorities(priorityRange) {
    if (priorityRange.normal) {
      this.PRIORITIES[PriorityType.NORMAL].upperValue = priorityRange.normal;
      this.PRIORITIES[PriorityType.MEDIUM].bottomValue = priorityRange.normal + 1;
    }
    if (priorityRange.medium) {
      this.PRIORITIES[PriorityType.MEDIUM].upperValue = priorityRange.medium;
      this.PRIORITIES[PriorityType.HIGH].bottomValue = priorityRange.medium + 1;
    }
  }

  build(): EAFilter {
    const fields = this.values
      // to object
      .map(priority => this.PRIORITIES[priority])
      // sort priority
      .sort((p1, p2) => (p1.order < p2.order ? -1 : 1))
      // reduce range process
      .reduce((result, range) => {
        // if empty add first range
        if (result.length === 0) {
          return [
            {
              bottomValue: range.bottomValue,
              upperValue: range.upperValue,
            },
          ];
        }
        // extract all range expect last
        const rest = result.slice(0, result.length - 1) || [];
        // get last range
        // destructuring notation : reverse list and get first element
        // don't use rest element with destructuring notation to keep ordering
        const [last /**...rest */] = [...result].reverse();
        // if last upperValue is equal to new bottomValue
        // simplify last range
        if (last.upperValue + 1 === range.bottomValue) {
          return [
            ...rest,
            {
              bottomValue: last.bottomValue,
              upperValue: range.upperValue,
            },
          ];
        }
        // otherwise add new range
        return [
          ...rest,
          last,
          {
            bottomValue: range.bottomValue,
            upperValue: range.upperValue,
          },
        ];
      }, new Array())
      // transform to object filter
      .map(range =>
        buildEAFilter('int-range', RequestFields.WEIGHT, {
          from: range.bottomValue,
          to: range.upperValue !== Infinity ? range.upperValue : 1000000,
        })
      );
    return {
      operator: FilterOperator.OR,
      fields,
    };
  }

  isEmpty(): boolean {
    return !isArray(this.values) || this.values.length === 0 || this.values.length >= Object.keys(PriorityType).length;
  }

  toPriority(priorityStr: string): PriorityType {
    const priority = Number(priorityStr);
    if (isNaN(priority) || priority <= this.PRIORITIES[PriorityType.NORMAL].upperValue) {
      return PriorityType.NORMAL;
    }
    if (priority <= this.PRIORITIES[PriorityType.MEDIUM].upperValue) {
      return PriorityType.MEDIUM;
    }
    return PriorityType.HIGH;
  }
}

export class AccountIdFilter implements BasketFilter {
  private _id: number | null = null;

  constructor(public value: string = '') {
    // return null if value isn't a valid number
    this._id = parseInt(value, 10);
  }

  build(): EAFilter {
    const id = this._id || 0;
    let field = buildEAFilter('int', 'responsible.accountId', id, CriteriaOperator.TERM);
    return {
      operator: FilterOperator.AND,
      fields: [field],
    };
  }

  isEmpty(): boolean {
    return !this._id;
  }
}

export class ContentFilter implements BasketFilter {
  constructor(public value: string = '') {}

  build(): EAFilter {
    return {
      operator: FilterOperator.AND,
      nested: {
        path: EpticaFields.EVENT,
      },
      semanticFields: [
        {
          query: this.value.slice(0, 255),
          targetsFields: [
            {
              fieldName: EpticaFields.EVENT_TEXT,
            },
          ],
        },
      ],
    };
  }

  isEmpty(): boolean {
    return !isString(this.value) || this.value.length === 0;
  }
}

export class BookmarkFilter implements BasketFilter {
  constructor(private readonly _bookmark: boolean = false) {}

  build(): EAFilter {
    const field = buildEAFilter('boolean', RequestFields.BOOKMARK, true, CriteriaOperator.MATCH);
    return {
      operator: FilterOperator.AND,
      fields: [field],
    };
  }

  isEmpty(): boolean {
    return !this._bookmark;
  }
}

/**
 * The TeamFilter is a 'Quick Filter' : that just means it won't be saved into the database, but it behaves like
 * a real filter.
 */
export class TeamQuickFilter implements BasketFilter {
  constructor(public readonly value: string = '') {}

  build(): EAFilter {
    const field = buildEAFilter('string', RequestFields.PERIMETERS, this.value, CriteriaOperator.CONTAINS);
    return {
      operator: FilterOperator.OR,
      fields: [field],
    };
  }

  isEmpty(): boolean {
    return !isString(this.value) || this.value.length === 0;
  }
}
